import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n) => "$" + Number(n || 0).toFixed(2);

// Self-contained customer booking flow for the Barber / personal-services vertical:
// pick service -> pick provider (or no preference) -> pick open slot -> contact -> confirm.
export default function AppointmentBooking() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const PRO = store?.provider_noun || ((store?.cart_flows || []).includes("call") ? "Professional" : "Barber");
  const [services, setServices] = useState(null);
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [providerId, setProviderId] = useState(() => new URLSearchParams(window.location.search).get("provider") || "any");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slotData, setSlotData] = useState(null);
  const [slot, setSlot] = useState(null);
  const [slotProvider, setSlotProvider] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "" });
  const [confirmed, setConfirmed] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/tenant/${slug}`).then((r) => r.json()).then((d) => {
      setStore({ name: d.name, logo: d.logo_url, color: d.primary_color || "#111", cart_type: d.cart_type, cart_flows: d.cart_flows || [], provider_noun: d.provider_noun, product_name: d.product_name, widgets: d.widgets || {} });
      document.title = `Book — ${d.name || "Appointments"}`;
    }).catch(() => {});
    fetch(`${API}/api/public/tenant/${slug}/appt-info`).then((r) => (r.ok ? r.json() : null)).then(setInfo).catch(() => {});
  }, [slug]);

  useEffect(() => {
    fetch(`${API}/api/public/tenant/${slug}/services`).then((r) => (r.ok ? r.json() : [])).then((d) => setServices(Array.isArray(d) ? d.filter((s) => s.active !== false) : [])).catch(() => setServices([]));
  }, [slug]);

  const loadSlots = (svc, pid, d) => {
    setLoading(true); setErr(""); setSlot(null); setSlotData(null);
    fetch(`${API}/api/public/tenant/${slug}/appointment-slots?service_id=${svc.id}&date=${d}&provider_id=${pid || "any"}`)
      .then((r) => r.json()).then((data) => setSlotData(data)).catch(() => setErr("Could not load times.")).finally(() => setLoading(false));
  };

  const pickService = (svc) => { setService(svc); setStep(2); };
  const pickProvider = (pid) => { setProviderId(pid); setStep(3); loadSlots(service, pid, date); };
  const changeDate = (d) => { setDate(d); if (service) loadSlots(service, providerId, d); };
  const pickSlot = (s, provName) => { setSlot(s); setSlotProvider(provName); setStep(4); };

  const submit = () => {
    if (!form.customer_name.trim()) { setErr("Please enter your name."); return; }
    setLoading(true); setErr("");
    const body = { service_id: service.id, provider_id: providerId, date, start: slot, ...form };
    fetch(`${API}/api/public/tenant/${slug}/appointments`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    }).then(async (r) => {
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not book.");
      setConfirmed(d); setStep(5);
    }).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };

  const color = store?.color || "#111";
  const wrap = { maxWidth: 680, margin: "0 auto", padding: "28px 18px", fontFamily: "system-ui,-apple-system,sans-serif", color: "#111" };
  const card = { border: "1px solid #eee", borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer", transition: "border-color .15s, box-shadow .15s" };
  const btn = { background: color, color: "#fff", border: 0, borderRadius: 999, padding: "12px 22px", fontSize: 15, cursor: "pointer", fontWeight: 600 };

  const Stepper = () => (
    <div style={{ display: "flex", gap: 6, marginBottom: 20 }} data-testid="booking-stepper">
      {["Service", PRO, "Time", "You", "Done"].map((lbl, i) => (
        <div key={lbl} style={{ flex: 1, height: 4, borderRadius: 4, background: step >= i + 1 ? color : "#e7e7e7" }} />
      ))}
    </div>
  );

  if (store && store.widgets?.appointments === false) {
    return <div style={wrap} data-testid="booking-unavailable"><h1>This page isn't available.</h1></div>;
  }
  if (store && store.cart_type && !(store.cart_flows || []).some((f) => f === "appointment" || f === "call")) {
    return <div style={wrap}><h1>Online booking isn't enabled here.</h1><p style={{ color: "#666" }}>This store isn't set up for appointments.</p></div>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <div style={wrap}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          {store?.logo && <img src={store.logo} alt="" width="40" height="40" style={{ borderRadius: 10, objectFit: "cover" }} />}
          <h1 style={{ fontSize: 22, margin: 0 }} data-testid="booking-store-name">{store?.name || "\u00A0"}</h1>
        </div>
        <Stepper />
        {err && <div style={{ background: "#fdecec", color: "#b00", padding: "10px 12px", borderRadius: 10, marginBottom: 12, fontSize: 14 }} data-testid="booking-error">{err}</div>}

        {step === 1 && (
          <div data-testid="step-service">
            <h2 style={{ fontSize: 17, marginBottom: 12 }}>Choose a service</h2>
            {services === null && <p style={{ color: "#888" }}>Loading…</p>}
            {services && services.length === 0 && <p style={{ color: "#888" }}>No services available yet. Please check back soon.</p>}
            {services && services.map((s) => (
              <div key={s.id} style={card} onClick={() => pickService(s)} data-testid={`service-${s.id}`}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = color)} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#eee")}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                  <div><strong>{s.name}</strong>{s.description && <div style={{ color: "#666", fontSize: 14 }}>{s.description}</div>}
                    <div style={{ color: "#888", fontSize: 13, marginTop: 2 }}>{s.duration_min} min</div></div>
                  <div style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{Number(s.price) ? money(s.price) : "Free"}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-provider">
            <button style={{ ...btn, background: "transparent", color: "#666", padding: 0, marginBottom: 10 }} onClick={() => setStep(1)}>&larr; Back</button>
            <h2 style={{ fontSize: 17, marginBottom: 12 }}>Choose your {PRO.toLowerCase()}</h2>
            <div style={card} onClick={() => pickProvider("any")} data-testid="provider-any"
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = color)} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#eee")}>
              <strong>No preference</strong><div style={{ color: "#666", fontSize: 14 }}>First available {PRO.toLowerCase()}</div>
            </div>
            <ProviderList slug={slug} pick={pickProvider} color={color} />
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-time">
            <button style={{ ...btn, background: "transparent", color: "#666", padding: 0, marginBottom: 10 }} onClick={() => setStep(2)}>&larr; Back</button>
            <h2 style={{ fontSize: 17, marginBottom: 12 }}>Pick a time</h2>
            <input type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => changeDate(e.target.value)}
              style={{ padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, marginBottom: 14, fontSize: 15 }} data-testid="booking-date" />
            {loading && <p style={{ color: "#888" }}>Loading times…</p>}
            {!loading && slotData && slotData.providers.length === 0 && <p style={{ color: "#888" }} data-testid="no-slots">No open times that day — try another date.</p>}
            {!loading && slotData && slotData.providers.map((p) => (
              <div key={p.provider_id} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{p.provider_name}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {p.slots.map((s) => (
                    <button key={s} onClick={() => { setProviderId(p.provider_id); pickSlot(s, p.provider_name); }}
                      style={{ border: `1px solid ${color}`, color, background: "#fff", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontSize: 14 }}
                      data-testid={`slot-${p.provider_id}-${s}`}>{s}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-contact">
            <button style={{ ...btn, background: "transparent", color: "#666", padding: 0, marginBottom: 10 }} onClick={() => setStep(3)}>&larr; Back</button>
            <h2 style={{ fontSize: 17, marginBottom: 6 }}>Your details</h2>
            <p style={{ color: "#666", fontSize: 14, marginTop: 0 }}>{service?.name} · {slotProvider} · {date} at {slot}</p>
            {["customer_name", "customer_phone", "customer_email"].map((f, i) => (
              <input key={f} placeholder={["Full name *", "Phone", "Email"][i]} value={form[f]}
                onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                style={{ display: "block", width: "100%", boxSizing: "border-box", padding: "12px 14px", border: "1px solid #ddd", borderRadius: 10, marginBottom: 10, fontSize: 15 }}
                data-testid={`input-${f}`} />
            ))}
            {service?.deposit > 0 && info?.deposit_enabled && (
              <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, marginBottom: 10, fontSize: 14 }} data-testid="booking-deposit-info">
                <strong>{money(service.deposit)} deposit required</strong>
                <div style={{ color: "#666", fontSize: 13, marginTop: 4 }}>You'll get a link to pay it after booking. {info.deposit_policy}</div>
              </div>
            )}
            {info && <p style={{ color: "#888", fontSize: 12 }} data-testid="booking-cancel-policy">You can cancel or reschedule online up to {info.cancel_window_hours} hours before your appointment.</p>}
            <button style={{ ...btn, width: "100%", marginTop: 6 }} onClick={submit} disabled={loading} data-testid="confirm-booking">
              {loading ? "Booking…" : "Confirm booking"}
            </button>
          </div>
        )}

        {step === 5 && confirmed && (
          <div data-testid="step-confirmed" style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 44 }}>✓</div>
            <h2 style={{ fontSize: 20 }}>You're booked!</h2>
            <p style={{ color: "#444" }}>{confirmed.service_name} with <strong>{confirmed.provider_name}</strong><br />
              {confirmed.date} at {confirmed.start}–{confirmed.end}</p>
            {confirmed.ref && <p style={{ fontSize: 13, color: "#666" }}>Reference <strong data-testid="booking-ref">{confirmed.ref}</strong></p>}
            {confirmed.deposit > 0 && (
              <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 14, margin: "14px auto", maxWidth: 420, textAlign: "left" }} data-testid="confirmed-deposit">
                <strong>{money(confirmed.deposit)} deposit due</strong>
                {info?.deposit_instructions && <div style={{ color: "#555", fontSize: 14, marginTop: 4 }}>{info.deposit_instructions}</div>}
                {info?.deposit_link && <a href={info.deposit_link} target="_blank" rel="noreferrer" style={{ ...btn, display: "inline-block", marginTop: 10, textDecoration: "none", background: "#0F766E" }} data-testid="pay-deposit-btn">Pay deposit</a>}
                <div style={{ color: "#888", fontSize: 12, marginTop: 8 }}>{info?.deposit_policy}</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 12 }}>
              {confirmed.calendar_url && <a href={confirmed.calendar_url} target="_blank" rel="noreferrer" style={{ ...btn, background: "#444", textDecoration: "none" }} data-testid="add-gcal-btn">Add to Google Calendar</a>}
              {confirmed.ics_url && <a href={confirmed.ics_url} style={{ ...btn, background: "transparent", color, border: `1px solid ${color}`, textDecoration: "none" }} data-testid="download-ics-btn">Apple / Outlook (.ics)</a>}
            </div>
            {confirmed.manage_url && <p style={{ marginTop: 14 }}><a href={confirmed.manage_url} style={{ color }} data-testid="manage-appt-link">Manage or cancel this appointment</a></p>}
            {confirmed.customer?.email && <p style={{ color: "#888", fontSize: 13 }}>A confirmation was emailed to {confirmed.customer.email}.</p>}
            <button style={{ ...btn, marginTop: 12 }} onClick={() => { setStep(1); setService(null); setSlot(null); setConfirmed(null); setForm({ customer_name: "", customer_phone: "", customer_email: "" }); }} data-testid="book-another-btn">Book another</button>
          </div>
        )}
        <div style={{ marginTop: 40, color: "#767676", fontSize: 12, textAlign: "center" }}>Powered by {store?.product_name || "MorrCart"}</div>
      </div>
    </div>
  );
}

function ProviderList({ slug, pick, color }) {
  const [provs, setProvs] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/public/tenant/${slug}/providers`).then((r) => (r.ok ? r.json() : [])).then((d) => setProvs(Array.isArray(d) ? d.filter((p) => p.active !== false) : [])).catch(() => {});
  }, [slug]);
  return provs.map((p) => (
    <div key={p.id} style={{ border: "1px solid #eee", borderRadius: 14, padding: 16, marginBottom: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
      onClick={() => pick(p.id)} data-testid={`provider-${p.id}`}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = color)} onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#eee")}>
      {p.avatar ? <img src={p.avatar} alt="" width="40" height="40" style={{ borderRadius: 999, objectFit: "cover" }} /> : <div style={{ width: 40, height: 40, borderRadius: 999, background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{(p.name || "?")[0]}</div>}
      <div><strong>{p.name}</strong>{p.bio && <div style={{ color: "#666", fontSize: 13 }}>{p.bio}</div>}</div>
    </div>
  ));
}
